import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
// Constants - Functions
import { WEB_SERVICE_MIMS } from "../../../../../../config/constants";
import { AISNProtocols } from "../../../../../../config/validateProtocols";
// Components
import {
  Header,
  HeaderTitle,
  MainDiv,
  FunctionsDiv,
  FunctionButton,
  FunctionText,
  InfoImg,
  ActionsDiv,
  CancelButton,
  SaveButton,
} from "./styles";
import UseFetch from "../../../../../../config/useFetch";
import Popup from "../../../../../popup";
import ProtocolPrescription from "./sections/protocolPrescription";
import ProtocolFunctions from "./sections/protocolFunctions";
import PrescriptionReason from "./sections/prescriptionReason";
// Assets
import InfoIcon from "../../../../../../assets/icons/ico_info_blue.svg";

const PrescriptionsPopup = ({
  prescriptions,
  currentPrescriptions,
  prescriptionStartingDate,
  prescriptionEndingDate,
  isSomePrescriptionInserted,
  isShowingPrescriptionsPopup,
  setIsShowingPrescriptionsPopup,
  refreshPrescriptions,
  setRefreshPrescriptions,
  hasRecsys,
}) => {
  let [prescriptionsToDatabase, setPrescriptionsToDatabase] = useState([]);
  const [isShowingFunctionsPopup, setIsShowingFunctionsPopup] = useState(false);
  const [isShowingReasonPopup, setIsShowingReasonPopup] = useState(false);
  const { patientId } = useParams();

  // Set prescriptions to save in database
  useEffect(() => {
    setPrescriptionsToDatabase(currentPrescriptions);
    return () => {
      setPrescriptionsToDatabase([]);
    };
  }, [currentPrescriptions]);

  const renderFunctionsButton = () => {
    return (
      <FunctionsDiv>
        <FunctionButton onClick={() => setIsShowingFunctionsPopup(true)}>
          <FunctionText>Motor / Cognitive functions</FunctionText>
          <InfoImg src={InfoIcon} alt="info-ico" />
        </FunctionButton>
      </FunctionsDiv>
    );
  };

  const renderHeader = () => {
    return (
      <Header>
        <HeaderTitle width="290px" padding="0 0 0 133px">
          ACTIVITY
        </HeaderTitle>
        <HeaderTitle width="190px">WEEK DAYS</HeaderTitle>
        <HeaderTitle width="140px">FUNCTIONS</HeaderTitle>
        <HeaderTitle width="72px" textAlign="center">
          TIME
        </HeaderTitle>
      </Header>
    );
  };

  const getProtocolPrescriptions = (protocolId) => {
    return prescriptions.filter((prescription) => prescription.PROTOCOL_ID === protocolId);
  };

  const renderBasicProtocols = () => {
    const { basicProtocols } = AISNProtocols();

    return (
      <>
        {renderHeader()}
        {basicProtocols.map((protocolId) => {
          const protocolPrescription = getProtocolPrescriptions(protocolId);
          const prescriptionDuration = protocolPrescription.length ? protocolPrescription[0].PRESCRIPTION_DURATION : 300;
          const prescriptionDays = protocolPrescription.length ? protocolPrescription[0].PRESCRIPTION_DAYS : [];

          return (
            <ProtocolPrescription
              key={protocolId}
              protocolId={protocolId}
              prescriptionDuration={prescriptionDuration}
              prescriptionDays={prescriptionDays}
              prescriptionStartingDate={prescriptionStartingDate}
              prescriptionEndingDate={prescriptionEndingDate}
              prescriptionsToDatabase={prescriptionsToDatabase}
            />
          );
        })}
      </>
    );
  };

  const renderArProtocols = () => {
    const { arProtocols } = AISNProtocols();

    return (
      <>
        {renderHeader()}
        {arProtocols.map((protocolId) => {
          const protocolPrescription = getProtocolPrescriptions(protocolId);
          const prescriptionDuration = protocolPrescription.length ? protocolPrescription[0].PRESCRIPTION_DURATION : 300;
          const prescriptionDays = protocolPrescription.length ? protocolPrescription[0].PRESCRIPTION_DAYS : [];

          return (
            <ProtocolPrescription
              key={protocolId}
              protocolId={protocolId}
              prescriptionDuration={prescriptionDuration}
              prescriptionDays={prescriptionDays}
              prescriptionStartingDate={prescriptionStartingDate}
              prescriptionEndingDate={prescriptionEndingDate}
              prescriptionsToDatabase={prescriptionsToDatabase}
            />
          );
        })}
      </>
    );
  };

  const savePrescriptions = async () => {
    if (prescriptionsToDatabase.length) {
      if (!isSomePrescriptionInserted) {
        await UseFetch("put", `${WEB_SERVICE_MIMS}/recsys/update-prescriptions-status/${patientId}`, new AbortController(), {
          startingDate: prescriptionStartingDate,
          endingDate: prescriptionEndingDate,
          status: "Rejected",
        }).catch((error) => {
          toast.error("Error sending data, please try again");
          console.log("Update prescriptions status: ", error);
        });
      }

      await UseFetch("post", `${WEB_SERVICE_MIMS}/prescriptions/add/${patientId}/plus`, new AbortController(), prescriptionsToDatabase)
        .then(({ data }) => {
          if (data.success) {
            toast.success("Prescriptions saved successfully");
            setIsShowingPrescriptionsPopup(false);
            setRefreshPrescriptions(!refreshPrescriptions);
          } else {
            toast.error("Error sending data, please try again");
          }
        })
        .catch((error) => {
          toast.error("Error sending data, please try again");
          console.log("Get current prescriptions data: ", error);
        });
    }
  };

  const renderActionButtons = () => {
    const saveAction = () => {
      if (hasRecsys) {
        setIsShowingReasonPopup(true);
      } else {
        savePrescriptions();
      }
    };

    return (
      <ActionsDiv>
        <CancelButton onClick={() => setIsShowingPrescriptionsPopup(false)}>Cancel</CancelButton>
        <SaveButton onClick={saveAction}>Continue</SaveButton>
      </ActionsDiv>
    );
  };

  return (
    <>
      <Popup isShowed={isShowingPrescriptionsPopup} setIsShowed={setIsShowingPrescriptionsPopup} backgroundId="prescriptionsPopup">
        <MainDiv>
          {renderFunctionsButton()}
          {renderBasicProtocols()}
          {renderArProtocols()}
          {renderActionButtons()}
        </MainDiv>
      </Popup>
      {isShowingFunctionsPopup && (
        <ProtocolFunctions isShowingFunctionsPopup={isShowingFunctionsPopup} setIsShowingFunctionsPopup={setIsShowingFunctionsPopup} />
      )}
      {isShowingReasonPopup && (
        <PrescriptionReason
          savePrescriptions={savePrescriptions}
          isShowingReasonPopup={isShowingReasonPopup}
          setIsShowingReasonPopup={setIsShowingReasonPopup}
        />
      )}
    </>
  );
};

export default PrescriptionsPopup;
